import React, { ComponentType, useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FieldError } from 'react-hook-form/dist/types'
import { isEqual, clone } from 'lodash'

import { getValueFromNestedObjectProp } from '~utils/getValueFromNestedObjectProp'
import { isFormFieldRequiredBySchema } from '~utils/isFormFieldRequiredBySchema'
import { FormContext, FormPrefixContext } from '~shared/components/reactHookForm/FormContext'

/**
 * README
 *
 * withFormFunctionality is a wrapper for react-hook-form(rhf) input fields
 * it helps with:
 * - nested naming -- by adding the formPrefixContext
 * - field requiredness -- based on yup schema
 * - field error -- provided by yup and found in react-hook-form(rhf) useFormContext
 */

interface FormElementDefaultProps {
  name: string
  label?: string
  helperText?: string
}

interface FormElementInjectedProps {
  required?: boolean
  validationError?: FieldError
}

export type FormElementProps = FormElementInjectedProps & FormElementDefaultProps;

// eslint-disable-next-line @typescript-eslint/ban-types -- object is valid here
export function withFormFunctionality<P extends object>(
  WrappedComponent: ComponentType<P & FormElementProps>,
): React.FC<P & FormElementDefaultProps> {
  return (props) => {
    const { getValues, errors } = useFormContext()
    const formContext = useContext(FormContext)
    const formPrefixContext = useContext(FormPrefixContext)

    const fieldNameWithPrefix = formPrefixContext
      ? `${formPrefixContext}.${props.name}`
      : `${props.name}`

    // get if input is required by evaluating the yup schema
    // circular required fields should be solved via yup
    const [fieldIsRequired, setFieldIsRequired] = useState<boolean>(false)
    useEffect(() => {
      setFieldIsRequired(
        !!formContext && !!formContext.schema
          && isFormFieldRequiredBySchema(
            fieldNameWithPrefix,
            formContext.schema,
            getValues(),
          ),
      )
    }, [fieldNameWithPrefix, formContext.schema, getValues, formContext])

    // evaluate if we have to rerender because of new errors
    // the rhf error object dos´nt trigger the useEffect since it is a Immutable object
    const [prevErrors, setPrevErrors] = useState({})
    const newErrors = isEqual(errors, prevErrors) ? errors : clone(errors)
    const [validationError, setValidationError] = useState<FieldError | undefined>(undefined)
    useEffect(() => {
      setPrevErrors(clone(errors))
      setValidationError(getValueFromNestedObjectProp(errors, fieldNameWithPrefix))
    }, [newErrors, fieldNameWithPrefix, errors])

    return <WrappedComponent
      validationError={validationError}
      required={fieldIsRequired}
      {...props}
      name={fieldNameWithPrefix}
    />
  }
}

export default withFormFunctionality
